import { Subject } from 'rxjs';

import { ToursSearch } from 'package-types';

import { Search } from './search';
import { SearchTtlCollection } from './search-ttl-collection';
import { TooLongSearchError } from '@services/tours-search/errors/too-long-search.error';
import { SearchResultResponse } from '@interfaces/tours-search-result';

export class SearchCollection {
  private readonly items = new Map<string, Search>();
  private readonly ttlCollection = new SearchTtlCollection();
  private readonly destroySearch$$ = new Subject<string>();

  readonly destroySearch$ = this.destroySearch$$.asObservable();

  forEach(callback: (search: Search) => void) {
    this.items.forEach(callback);
  }

  get(id: string, page: number): Search | undefined {
    return this.items.get(this.createKey(id, page));
  }

  has(id: string): boolean {
    return Array.from(this.items.values()).some(search => search.getSearchId() === id);
  }

  isEmpty(): boolean {
    return this.items.size === 0;
  }

  consumeResult(expectedSearch: Search, response: SearchResultResponse) {
    const id = expectedSearch.getSearchId();
    const page = expectedSearch.getPageNumber();
    const search = this.items.get(this.createKey(id, page));
    if (search !== expectedSearch) {
      return;
    }
    if (response.status === ToursSearch.ResultStatus.Ready || response.status === ToursSearch.ResultStatus.Failed) {
      search.finish(response);
      this.delete(id, page);
    } else {
      search.next(response);
    }
  }

  set(search: Search): void {
    const searchId = search.getSearchId();
    const key = this.createKey(searchId, search.getPageNumber());
    this.delete(searchId, search.getPageNumber());
    this.items.set(key, search);
    this.ttlCollection.add(key, () => this.destroyByTimeout(key));
  }

  private destroyByTimeout(key: string) {
    const search = this.items.get(key);
    if (search) {
      const searchId = search.getSearchId();
      search.error(new TooLongSearchError(search.getSearchId(), search.getPageNumber(), search.getParams()));
      this.items.delete(key);
      this.emitDestroySearchIfUnused(searchId);
    }
  }

  delete(id: string, page: number): void {
    const key = this.createKey(id, page);
    const search = this.items.get(key);
    if (search) {
      search.destroy();
      this.items.delete(key);
      this.ttlCollection.delete(key);
      this.emitDestroySearchIfUnused(id);
    }
  }

  destroy(): void {
    for (const search of Array.from(this.items.values())) {
      this.delete(search.getSearchId(), search.getPageNumber());
    }
  }

  private createKey(id: string, page: number): string {
    return `${id}:${page}`;
  }

  private emitDestroySearchIfUnused(id: string): void {
    if (!this.has(id)) {
      this.destroySearch$$.next(id);
    }
  }
}
